// URL-state contract for the /blogs listing page. Every filter (category,
// search, page, results-per-page) lives in the query string so results are
// crawlable, linkable, and shareable — not hidden behind client-only state.
export interface BlogQuery {
  category?: string;
  q?: string;
  page?: number;
  perPage?: number;
}

export const DEFAULT_PER_PAGE = 9;
export const PER_PAGE_OPTIONS = [9, 12, 24] as const;

type RawSearchParams = Record<string, string | string[] | undefined>;

function first(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

export function parseBlogQuery(sp: RawSearchParams): BlogQuery {
  const category = first(sp.category)?.trim() || undefined;
  const q = first(sp.q)?.trim() || undefined;
  const pageRaw = Number(first(sp.page));
  const perPageRaw = Number(first(sp.perPage));
  return {
    category,
    q,
    page: Number.isInteger(pageRaw) && pageRaw > 0 ? pageRaw : 1,
    perPage: PER_PAGE_OPTIONS.includes(perPageRaw as (typeof PER_PAGE_OPTIONS)[number])
      ? perPageRaw
      : DEFAULT_PER_PAGE,
  };
}

/** Build a /blogs href from a base query with overrides applied. Omits params
 *  at their default value so URLs stay clean (canonical-friendly). */
export function buildBlogHref(base: BlogQuery, overrides: Partial<BlogQuery>): string {
  const merged = { ...base, ...overrides };
  const params = new URLSearchParams();
  if (merged.category) params.set("category", merged.category);
  if (merged.q) params.set("q", merged.q);
  if (merged.perPage && merged.perPage !== DEFAULT_PER_PAGE) {
    params.set("perPage", String(merged.perPage));
  }
  if (merged.page && merged.page > 1) params.set("page", String(merged.page));
  const qs = params.toString();
  return qs ? `/blogs?${qs}` : "/blogs";
}
